import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Grid as MuiGrid,
  Chip,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Preview as PreviewIcon } from '@mui/icons-material';
import { groupService, GroupCriteriaOptions } from '../../services/groupService';
import emailTemplateService, { EmailTemplate } from '../../services/emailTemplateService';
import campaignService from '../../services/campaignService';

const Grid = MuiGrid;

/** Properties available in email campaign audience filters. Empty selection = all of these. */
const EMAIL_CAMPAIGN_PROPERTY_CODES = [
  'TB',
  'AP',
  'CC',
  'AW',
  'GF',
  'AL',
  'AA',
  'HR',
  'AF',
  'DB',
  'OC',
  'AN',
] as const;

type CriteriaProperty = { id: number; name: string; property_id: string };

function buildCriteriaPayload(params: {
  propertyIds: number[];
  allowedPropertyIds: number[];
  guestCountries: string[];
  guestStates: string[];
  guestCities: string[];
  statuses: string[];
  startDate: string;
  endDate: string;
  marketSegments: string[];
  bookingTypes: string[];
}): Record<string, unknown> {
  const c: Record<string, unknown> = {};
  // No property selected means all allowed campaign properties are selected
  c.property_ids = params.propertyIds.length
    ? params.propertyIds
    : params.allowedPropertyIds;
  if (params.guestCountries.length) c.guest_countries = params.guestCountries;
  if (params.guestStates.length) c.guest_states = params.guestStates;
  if (params.guestCities.length) c.guest_cities = params.guestCities;
  if (params.statuses.length) c.status = params.statuses;
  if (params.startDate) c.start_date = params.startDate;
  if (params.endDate) c.end_date = params.endDate;
  if (params.marketSegments.length) c.market_segments = params.marketSegments;
  if (params.bookingTypes.length) c.booking_types = params.bookingTypes;
  return c;
}

const CreateCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audienceGroupName, setAudienceGroupName] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState<number | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [criteriaOptions, setCriteriaOptions] = useState<GroupCriteriaOptions | null>(null);

  const [propertyIds, setPropertyIds] = useState<number[]>([]);
  const [guestCountries, setGuestCountries] = useState<string[]>([]);
  const [guestStates, setGuestStates] = useState<string[]>([]);
  const [guestCities, setGuestCities] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [marketSegments, setMarketSegments] = useState<string[]>([]);
  const [bookingTypes, setBookingTypes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const propsList = useMemo(() => {
    const all = (criteriaOptions?.properties || []) as CriteriaProperty[];
    const allowed = new Set(EMAIL_CAMPAIGN_PROPERTY_CODES.map((c) => c.toUpperCase()));
    return all.filter((p) => allowed.has(String(p.property_id || '').toUpperCase()));
  }, [criteriaOptions]);

  const allowedPropertyIds = useMemo(() => propsList.map((p) => p.id), [propsList]);

  const criteriaPayload = useMemo(
    () =>
      buildCriteriaPayload({
        propertyIds,
        allowedPropertyIds,
        guestCountries,
        guestStates,
        guestCities,
        statuses,
        startDate,
        endDate,
        marketSegments,
        bookingTypes,
      }),
    [
      propertyIds,
      allowedPropertyIds,
      guestCountries,
      guestStates,
      guestCities,
      statuses,
      startDate,
      endDate,
      marketSegments,
      bookingTypes,
    ]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [templatesRes, optionsRes] = await Promise.all([
          emailTemplateService.getEmailTemplates({ is_active: 'true' }),
          groupService.getGroupCriteriaOptions(),
        ]);
        const t = templatesRes.results || templatesRes;
        setEmailTemplates(Array.isArray(t) ? t : []);
        setCriteriaOptions(optionsRes.data);
      } catch (e) {
        console.error(e);
        setError('Failed to load templates or filter options');
      }
    };
    load();
  }, []);

  const handlePreviewAudience = async () => {
    if (!allowedPropertyIds.length) {
      setError('Campaign properties are not available yet. Refresh and try again.');
      return;
    }
    try {
      setPreviewLoading(true);
      setError(null);
      const res = await groupService.previewAudience({
        ...criteriaPayload,
        campaign_type: 'email',
      });
      const count = (res as { data?: { count?: number } }).data?.count;
      setPreviewCount(typeof count === 'number' ? count : null);
      setSuccess(`Audience size: ${count ?? '?'} unique emails`);
    } catch (err: unknown) {
      console.error(err);
      setError('Could not preview audience');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Campaign name is required');
      return;
    }
    if (!emailTemplateId) {
      setError('Select an email template');
      return;
    }
    if (!allowedPropertyIds.length) {
      setError('Campaign properties are not available yet. Refresh and try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const created = await campaignService.createCampaign({
        name: name.trim(),
        description: description.trim(),
        campaign_type: 'email',
        email_template_id: emailTemplateId,
        audience_criteria: criteriaPayload,
        audience_group_name: audienceGroupName.trim() || undefined,
      });
      setSuccess('Campaign created');
      setTimeout(() => navigate(`/campaigns/${created.id}`), 800);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; [k: string]: unknown } } };
      const msg =
        ax.response?.data &&
        typeof ax.response.data === 'object' &&
        'message' in ax.response.data
          ? String(ax.response.data.message)
          : 'Failed to create campaign';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" onClick={() => navigate('/campaigns')} sx={{ cursor: 'pointer' }}>
          Campaigns
        </Link>
        <Typography color="text.primary">Create email campaign</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        Create email campaign
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a template with <code>{'{{ variables }}'}</code> and filters. Leave properties empty to
        include all campaign properties.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid style={{ gridColumn: 'span 12' }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Campaign
              </Typography>
              <Stack spacing={2}>
                <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
                <TextField
                  label="Audience group label (optional)"
                  value={audienceGroupName}
                  onChange={(e) => setAudienceGroupName(e.target.value)}
                  helperText="Shown as the auto-generated group name suffix"
                  fullWidth
                />
                <FormControl fullWidth required>
                  <InputLabel>Email template</InputLabel>
                  <Select
                    value={emailTemplateId ?? ''}
                    label="Email template"
                    onChange={(e) => setEmailTemplateId(Number(e.target.value))}
                  >
                    {emailTemplates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Audience filters (multi-select)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Leave properties empty to include all listed properties. Other filters are optional.
              </Typography>

              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  options={propsList}
                  getOptionLabel={(o) => `${o.name} (${o.property_id})`}
                  value={propsList.filter((p) => propertyIds.includes(p.id))}
                  onChange={(_, v) => setPropertyIds(v.map((p) => p.id))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Properties"
                      placeholder="All campaign properties (leave empty)"
                      helperText="Empty selection includes all campaign properties"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                    ))
                  }
                />

                <Autocomplete
                  multiple
                  freeSolo
                  options={criteriaOptions?.countries || []}
                  value={guestCountries}
                  onChange={(_, v) => setGuestCountries(v.map(String))}
                  renderInput={(params) => <TextField {...params} label="Guest countries" />}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={criteriaOptions?.states || []}
                  value={guestStates}
                  onChange={(_, v) => setGuestStates(v.map(String))}
                  renderInput={(params) => <TextField {...params} label="Guest states" />}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={criteriaOptions?.cities || []}
                  value={guestCities}
                  onChange={(_, v) => setGuestCities(v.map(String))}
                  renderInput={(params) => <TextField {...params} label="Guest cities" />}
                />

                <Autocomplete
                  multiple
                  options={criteriaOptions?.statuses || []}
                  value={statuses}
                  onChange={(_, v) => setStatuses(v)}
                  renderInput={(params) => <TextField {...params} label="Reservation status" />}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Arrival from (start_date)"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Departure by (end_date)"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>

                <Autocomplete
                  multiple
                  options={criteriaOptions?.market_segments || []}
                  value={marketSegments}
                  onChange={(_, v) => setMarketSegments(v)}
                  renderInput={(params) => <TextField {...params} label="Market segments" />}
                />
                <Autocomplete
                  multiple
                  options={criteriaOptions?.booking_types || []}
                  value={bookingTypes}
                  onChange={(_, v) => setBookingTypes(v)}
                  renderInput={(params) => <TextField {...params} label="Booking types" />}
                />

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={previewLoading ? <CircularProgress size={18} /> : <PreviewIcon />}
                    onClick={handlePreviewAudience}
                    disabled={previewLoading}
                  >
                    Preview audience count
                  </Button>
                  {previewCount !== null && (
                    <Chip color="primary" label={`${previewCount} unique emails`} />
                  )}
                </Stack>
              </Stack>
            </Paper>

            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Creating…' : 'Create campaign'}
              </Button>
              <Button type="button" variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate('/campaigns')}>
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success">{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateCampaign;
